// tokens.ts — the API tokens screen (#/tokens; work item 61, PR 1).
//
// Every signed-in role gets this page (a viewer's token is as weak as the
// viewer): list your personal access tokens, mint one (name + scope
// checkboxes read from the server's own catalog + optional expiry), see the
// plaintext EXACTLY ONCE in the show-once dialog (the invite-link copy
// idiom), revoke with confirm. The client hides nothing policy-shaped: the
// scope vocabulary comes from GET /api/pat-scopes, and every refusal the
// server answers is rendered verbatim.

import type { ColDef, GridApi } from 'ag-grid-community';
import { mkFcSelect, mkFcText, mkFcToggle, type FcToggleCtl } from './form-controls.js';
import { el, jsonFetch, makeGrid, renderRefusal, timeCell } from './admin-ui.js';
import { iconBtn, mkIconBtn, setBtnGlyph, setBtnLabel, setBtnState } from './buttons.js';
import {
  confirmDialog as dlgConfirm,
  destroyDialog,
  noticeDialog as dlgNotice,
  openDialog,
  showDialogMsg,
} from './dialog.js';
import { mountShell } from './shell.js';

export interface PatRow {
  id: string;
  name: string;
  token_prefix: string;
  token_last4: string;
  scopes: string[];
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
}

interface ScopeInfo {
  name: string;
  description: string;
}

/** Token state as ONE word, derived in one place so the pill, the row
 * styling and the action column cannot disagree. Exported for the headless
 * test. */
export function patState(p: PatRow, now = Date.now()): 'active' | 'revoked' | 'expired' {
  if (p.revoked_at) return 'revoked';
  if (p.expires_at && new Date(p.expires_at).getTime() <= now) return 'expired';
  return 'active';
}

/** The display form of a token: prefix…last4 — all the plaintext the list
 * surface ever holds. Exported for the headless test. */
export function patDisplay(p: Pick<PatRow, 'token_prefix' | 'token_last4'>): string {
  return `${p.token_prefix}…${p.token_last4}`;
}

function expiresCell(iso: string | null): HTMLElement {
  if (!iso) return el('span', 'hc-time', 'never');
  const t = new Date(iso).getTime();
  const e = el('span', 'hc-time', t <= Date.now() ? 'expired' : new Date(iso).toLocaleDateString());
  e.dataset.tip = new Date(iso).toLocaleString();
  return e;
}

export function buildPatColumns(ctx: { onRevoke: (row: PatRow) => void }): ColDef<PatRow>[] {
  return [
    {
      // Newest-first by default rides the Created column (below); Name
      // sorts alphabetically when clicked.
      colId: 'name', headerName: 'Name', flex: 2, minWidth: 180,
      valueGetter: (p) => p.data?.name ?? '',
    },
    {
      colId: 'token', headerName: 'Token', flex: 1.5, minWidth: 190, sortable: false,
      cellRenderer: (p: { data?: PatRow }) => {
        const e = el('code', 'hc-pat-token', p.data ? patDisplay(p.data) : '');
        e.dataset.tip = 'The full token was shown once at creation and is not stored.';
        return e;
      },
    },
    {
      colId: 'scopes', headerName: 'Scopes', flex: 2, minWidth: 220, sortable: false,
      cellRenderer: (p: { data?: PatRow }) => {
        const wrap = el('span', 'hc-pat-scopes');
        for (const s of p.data?.scopes ?? []) {
          wrap.appendChild(el('span', 'hc-pill', s));
        }
        wrap.dataset.tip = (p.data?.scopes ?? []).join(', ');
        return wrap;
      },
    },
    {
      colId: 'state', headerName: 'Status', flex: 0.8, minWidth: 110,
      valueGetter: (p) => (p.data ? patState(p.data) : ''),
      cellRenderer: (p: { data?: PatRow }) => {
        const e = document.createElement('span');
        if (!p.data) return e;
        const state = patState(p.data);
        // Reuses the account-state pill palette: active is active; a token
        // that no longer works (revoked/expired) reads as disabled.
        e.className = `hc-pill hc-pill-state-${state === 'active' ? 'active' : 'disabled'}`;
        e.textContent = state === 'active' ? 'Active' : state === 'revoked' ? 'Revoked' : 'Expired';
        return e;
      },
    },
    {
      colId: 'created', headerName: 'Created', flex: 1, minWidth: 120, sort: 'desc',
      comparator: (_a, _b, na, nb) => {
        const ta = na.data?.created_at ? Date.parse(na.data.created_at) : 0;
        const tb = nb.data?.created_at ? Date.parse(nb.data.created_at) : 0;
        return ta - tb;
      },
      valueGetter: (p) => p.data?.created_at ?? '',
      cellRenderer: (p: { data?: PatRow }) => timeCell(p.data?.created_at ?? null),
    },
    {
      colId: 'expires', headerName: 'Expires', flex: 1, minWidth: 110, sortable: false,
      cellRenderer: (p: { data?: PatRow }) => expiresCell(p.data?.expires_at ?? null),
    },
    {
      colId: 'lastUsed', headerName: 'Last used', flex: 1, minWidth: 120,
      comparator: (_a, _b, na, nb) => {
        const ta = na.data?.last_used_at ? Date.parse(na.data.last_used_at) : 0;
        const tb = nb.data?.last_used_at ? Date.parse(nb.data.last_used_at) : 0;
        return ta - tb;
      },
      valueGetter: (p) => p.data?.last_used_at ?? '',
      cellRenderer: (p: { data?: PatRow }) => {
        const cell = timeCell(p.data?.last_used_at ?? null);
        if (!p.data?.last_used_at) cell.textContent = 'never';
        return cell;
      },
    },
    {
      colId: 'actions', headerName: '', flex: 0.8, minWidth: 110, sortable: false,
      cellRenderer: (p: { data?: PatRow }) => {
        const wrap = el('span', 'hc-actions');
        if (!p.data || patState(p.data) !== 'active') return wrap;
        const row = p.data;
        // Was the console's one TEXT-label row action while every sibling
        // grid (users, templates) already used icon-only rows — the audit
        // named it off-system. Icon-only + the destructive ramp now.
        // The arm-then-fire gate is deliberately OFF: this click OPENS
        // revokeDialog, which asks with the consequence, and arming here
        // would make revoking a three-click double-confirm.
        const b = mkIconBtn({
          glyph: 'trash-2',
          label: `Revoke ${row.name}`,
          intent: 'destructive',
          size: 'sm',
          noConfirm: 'the click opens the Revoke confirmation dialog, which asks with the consequence',
          cls: 'hc-action',
        });
        b.addEventListener('click', () => ctx.onRevoke(row));
        wrap.appendChild(b);
        return wrap;
      },
    },
  ];
}

export function mountTokens(root: HTMLElement): () => void {
  const shell = mountShell(root, 'tokens');
  shell.pagebar.innerHTML = `
    <h1 class="hc-pagetitle">API tokens</h1>
    <div class="hc-status" id="hcPatStatus"></div>`;
  shell.main.classList.add('hc-admin');
  shell.main.innerHTML = `
    <div class="hc-toolbar" id="hcToolbar"></div>
    <div id="hcGrid" class="hc-grid"></div>`;
  const status = root.querySelector('#hcPatStatus') as HTMLElement;
  const toolbar = root.querySelector('#hcToolbar') as HTMLElement;
  const modal = shell.modal;

  let grid: GridApi<PatRow> | null = null;
  let rows: PatRow[] = [];

  toolbar.innerHTML = `
    <div class="hc-status">Personal access tokens authenticate API requests as you.
      Each carries only the scopes you grant it, and your role still applies.</div>
    <span class="hc-spacer"></span>
    ${iconBtn({
      // THE additive action of the tokens page — minting is the one thing
      // this screen creates.
      glyph: 'plus', label: 'New token', intent: 'additive', ctx: 'tokens-page',
      size: 'sm', id: 'hcNewToken',
    })}`;
  (toolbar.querySelector('#hcNewToken') as HTMLElement)
    .addEventListener('click', () => void mintDialog());

  async function load(): Promise<void> {
    const { status: st, data } = await jsonFetch<{ pats: PatRow[] }>('GET', '/api/pats');
    if (st === 401 || st === 403) return renderRefusal(root, st);
    rows = data?.pats ?? [];
    if (!grid) {
      grid = makeGrid<PatRow>(root.querySelector('#hcGrid') as HTMLElement, {
        columnDefs: buildPatColumns({ onRevoke: revokeDialog }),
        rowData: rows,
        getRowClass: (p) => (p.data && patState(p.data) !== 'active' ? 'hc-row-disabled' : ''),
      });
    } else {
      grid.setGridOption('rowData', rows);
    }
    const active = rows.filter((r) => patState(r) === 'active').length;
    status.textContent = `${rows.length} token${rows.length === 1 ? '' : 's'} (${active} active)`;
  }

  function revokeDialog(p: PatRow): void {
    dlgConfirm(modal, {
      title: `Revoke ${p.name}?`,
      body:
        `${patDisplay(p)} stops working immediately — any agent using it gets a 401 on ` +
        'its next request. This cannot be undone.',
      confirmLabel: 'Revoke',
      danger: true,
      onConfirm: () => {
        void (async () => {
          const r = await jsonFetch<{ error?: string }>('POST', `/api/pats/${p.id}/revoke`);
          if (r.status !== 200) return notice('Could not revoke', r.data?.error ?? `Refused (${r.status})`);
          return load();
        })();
      },
    });
  }

  async function mintDialog(): Promise<void> {
    const cat = await jsonFetch<{ scopes: ScopeInfo[] }>('GET', '/api/pat-scopes');
    const scopes = cat.data?.scopes ?? [];
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', 'New API token'));
      body.appendChild(el('p', 'hc-dialog-body',
        'The token is shown once, on creation. It can do at most what you can do, ' +
        'narrowed to the scopes you pick.'));
      const form = document.createElement('form');
      form.className = 'hc-form';

      const nameLabel = el('label', 'hc-field');
      nameLabel.appendChild(el('span', 'hc-field-label', 'Name'));
      // The form-control primitive (#1658 slice b); type=text by construction.
      const name = mkFcText({ label: 'Name', placeholder: 'my-local-agent' });
      name.input.required = true;
      name.input.maxLength = 120;
      name.input.setAttribute('data-autofocus', '');
      nameLabel.appendChild(name.el);
      form.appendChild(nameLabel);

      const scopesField = el('div', 'hc-field');
      scopesField.appendChild(el('span', 'hc-field-label', 'Scopes'));
      const list = el('div', 'hc-rolelist hc-scopelist');
      // A scope multi-pick, not a grid bulk-select: each row is an
      // independent capability the token either has or does not, so the
      // ruling's checkbox carve-out does not reach it — every row is a
      // toggle.
      const boxes: { name: string; box: FcToggleCtl }[] = [];
      for (const s of scopes) {
        const opt = el('div', 'hc-roleopt');
        const box = mkFcToggle({ label: s.name, text: '' });
        opt.appendChild(box.el);
        const txt = el('span');
        txt.appendChild(el('span', 'hc-roleopt-name', s.name));
        txt.appendChild(el('span', 'hc-roleopt-blurb', s.description));
        opt.appendChild(txt);
        list.appendChild(opt);
        boxes.push({ name: s.name, box });
      }
      scopesField.appendChild(list);
      form.appendChild(scopesField);

      const expLabel = el('label', 'hc-field');
      expLabel.appendChild(el('span', 'hc-field-label', 'Expires'));
      const exp = mkFcSelect({
        label: 'Expires',
        options: [
          { value: '', label: 'Never' }, { value: '7', label: 'In 7 days' },
          { value: '30', label: 'In 30 days' }, { value: '90', label: 'In 90 days' },
          { value: '365', label: 'In 1 year' },
        ],
        value: '90',
      });
      expLabel.appendChild(exp.el);
      form.appendChild(expLabel);

      const msg = el('p', 'hc-dialog-msg');
      form.appendChild(msg);

      const actions = el('div', 'hc-dialog-actions');
      // THE additive action of THIS dialog (per-dialog ctx). `type` rides
      // the spec: the primitive writes it, and a duplicate attribute loses.
      const submit = mkIconBtn({
        glyph: 'plus', label: 'Create token', intent: 'additive',
        ctx: 'new-token-dialog', type: 'submit',
      });
      const cancel = mkIconBtn({ glyph: 'x', label: 'Cancel' });
      cancel.addEventListener('click', close);
      actions.appendChild(cancel);
      actions.appendChild(submit);
      form.appendChild(actions);

      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const chosen = boxes.filter((b) => b.box.checked).map((b) => b.name);
        if (chosen.length === 0) {
          showDialogMsg(msg, 'Pick at least one scope.');
          return;
        }
        setBtnState(submit, 'busy');
        void jsonFetch<{ token?: string; pat?: PatRow; error?: string }>('POST', '/api/pats', {
          name: name.value.trim(),
          scopes: chosen,
          ...(exp.value ? { expires_days: Number(exp.value) } : {}),
        }).then((r) => {
          setBtnState(submit, 'enabled');
          if (r.status !== 201 || !r.data?.token) {
            showDialogMsg(msg, r.data?.error ?? `Refused (${r.status})`);
            return;
          }
          close();
          showTokenOnce(name.value.trim(), r.data.token);
          void load();
        });
      });
      body.appendChild(form);
    });
  }

  /** The show-once dialog (the invite-link idiom): selectable, copyable,
   * and loud that this is the only time the plaintext exists. */
  function showTokenOnce(name: string, token: string): void {
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', 'Token created'));
      body.appendChild(el('p', 'hc-dialog-body',
        `Copy ${name} now — it is shown this once and never stored. ` +
        'Use it as: Authorization: Bearer <token>'));
      const warn = el('div', 'hc-warn');
      warn.appendChild(el('strong', '', 'You will not see this token again.'));
      body.appendChild(warn);
      const row = el('div', 'hc-copyrow');
      // `.hc-copyfield` rides the primitive's input through the skin seat,
      // BESIDE the fc-* classes (#1658 slice b).
      const field = mkFcText({ label: 'Token', value: token, skin: { input: 'hc-copyfield' } });
      const input = field.input;
      input.readOnly = true;
      input.addEventListener('focus', () => input.select());
      const copy = mkIconBtn({ glyph: 'copy', label: 'Copy' });
      // Icon-only: the outcome rides the GLYPH and the label (one string,
      // tooltip and accessible name together) — a textContent write would
      // erase the sprite the button IS.
      const settle = (glyph: string, label: string): void => {
        setBtnGlyph(copy, glyph);
        setBtnLabel(copy, label);
        window.setTimeout(() => { setBtnGlyph(copy, 'copy'); setBtnLabel(copy, 'Copy'); }, 1600);
      };
      copy.addEventListener('click', () => {
        input.select();
        void navigator.clipboard?.writeText(input.value).then(
          () => settle('check', 'Copied'),
          () => settle('circle-alert', 'Press ⌘C'),
        );
      });
      row.appendChild(field.el);
      row.appendChild(copy);
      body.appendChild(row);
      const actions = el('div', 'hc-dialog-actions');
      // Acknowledgment, not creation — neutral (noticeDialog's precedent).
      const done = mkIconBtn({ glyph: 'check', label: 'Done', attrs: { 'data-act': 'ok' } });
      done.addEventListener('click', close);
      actions.appendChild(done);
      body.appendChild(actions);
    });
  }

  function notice(title: string, detail: string): void {
    dlgNotice(modal, { title, body: detail });
  }

  void load();
  return () => {
    destroyDialog(modal);
    shell.teardown();
  };
}
